import { BehaviorSubject, Subject } from 'rxjs';
import { logger } from './logger';
import { MotionMasterReqResSocket } from './motion-master-req-res-socket';
import { IMotionMasterMessage } from './types';
import { convertMotionMasterMessageToLoggerContext } from './util';

export class MotionMasterReqResWorkerSocket implements MotionMasterReqResSocket {
  readonly opened$ = new BehaviorSubject<boolean>(false);

  readonly close$ = new Subject<{ code: number; reason: string }>();

  readonly alive$ = new BehaviorSubject<boolean>(false);

  readonly message$ = new Subject<IMotionMasterMessage>();

  private _url?: string;
  private _pingSystemInterval?: number;
  private _systemAliveTimeout?: number;

  constructor(public readonly worker: Worker) {}

  get url(): string | undefined {
    return this._url;
  }

  get pingSystemInterval(): number | undefined {
    return this._pingSystemInterval;
  }

  get systemAliveTimeout(): number | undefined {
    return this._systemAliveTimeout;
  }

  open(url: string, pingSystemInterval = 250, systemAliveTimeout = 1000): void {
    this._url = url;
    this._pingSystemInterval = pingSystemInterval;
    this._systemAliveTimeout = systemAliveTimeout;

    this.worker.onmessage = ({ data }) => {
      if ('opened' in data) {
        const { opened } = data;
        this.opened$.next(opened);
        logger.info(opened ? `Opened req/res 🔌 ${url}` : `Closed req/res 🔌 ${url}`);
      } else if ('alive' in data) {
        const { alive } = data;
        this.alive$.next(alive);
        logger.info(alive ? `System is alive 💓` : 'System is as dead as 🦤');
      } else if ('message' in data) {
        const { message } = data;

        const context = convertMotionMasterMessageToLoggerContext(message);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logger.info(context as any, `Received ${context.name}`);

        this.message$.next(message);
      } else if ('close' in data) {
        const { close } = data;
        this.close$.next(close);
        logger.info(`Close event received on req/res 🔌 ${url} ${JSON.stringify(close)}`);
      }
    };

    this.worker.postMessage({
      open: { url, pingSystemInterval, systemAliveTimeout },
    });
  }

  reopen() {
    if (this.url && this.opened$.value === false) {
      this.open(this.url, this.pingSystemInterval, this.systemAliveTimeout);
    }
  }

  close(): void {
    this.worker.postMessage({ close: true });
  }

  send(message: IMotionMasterMessage): void {
    const context = convertMotionMasterMessageToLoggerContext(message);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    logger.info(context as any, `Send ${context.name}`);

    this.worker.postMessage({ message });
  }
}
